<template>
  <!-- prettier-ignore -->
  <div class="Detail">
    <a
      v-if="showBackButton"
      class="back global-sanitas-link"
      @click="$emit('backToBundle')"
      v-text="`< ${getLabel('main_back')}`"
    />
    <package-detail
      class="detail"
      :available="available"
      :item="$props.item"
      :products="$props.products"
      :skip-entry-page="$props.skipEntryPage"
      :skip-packages-overview="$props.skipPackagesOverview"
      @backToBundle="$emit('backToBundle')"
    />
    <div
      v-if="$props.item.isPersonDataEdited && !hasChosenPackage"
      class="update-person"
    >
      <a
        class="global-sanitas-link"
        @click="onEditPerson()"
        v-text="getLabel('detail_person_change')"
      />
    </div>
    <div class="actions">
      <p v-text="getLabel('detail_userinfo')" />
      <Form.Button
        class="button-detail"
        :primary="$props.item.isPersonDataEdited"
        :secondary="!$props.item.isPersonDataEdited"
        @click="buttonAction"
      >
        {{ buttonLabel }}
        <div
          v-show="hasChosenPackage"
          class="button-detail-loader"
        >
          <span>⚆</span>
        </div>
      </Form.Button>
    </div>
    <the-modal
      v-model="personModal"
      background-blur="div.PartnerWidget-container"
      locked
    >
      <loading-alt v-if="isSavingPersonData" />
      <person-edit
        v-else
        :basket="$props.item.basket"
        @save="savePerson"
      />
    </the-modal>
  </div>
</template>

<script setup>
import { computed, ref, provide } from 'vue'

import cloneDeep from 'lodash/cloneDeep'

import { ReactivityUtil } from '@/utils/Reactivity'

import basketStore from '@/store/basket'
import usePartnerWidget from '@/hooks/usePartnerWidget'
import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

import * as Form from '@/components/Form'
import LoadingAlt from '@/components/Container/LoadingAlt'
import PackageDetail from '@/modules/PartnerWidget/components/package/PackageDetail'
import PersonEdit from '@/modules/PartnerWidget/components/person/PersonEdit'

// HOOKS
const { getLabel, isPackageAvailable } = usePartnerWidget()
const { hasChosenPackage } = usePartnerWidgetBundleLoader()

// INIT
const emit = defineEmits(['backToBundle', 'editScreenDefinitions', 'goToSanitas'])
const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  openInSameWindow: {
    type: Boolean,
    default: false,
  },
  products: {
    type: Object,
    default: () => ({}),
  },
  skipEntryPage: {
    type: Boolean,
    default: false,
  },
  skipPackagesOverview: {
    type: Boolean,
    default: false,
  },
})

// DATA
const isSavingPersonData = ref(false)
const personModal = ref(false)

// COMPUTED
const available = computed(() => {
  return isPackageAvailable(props.item)
})

const buttonLabel = computed(() => {
  return props.item.isPersonDataEdited ? getLabel('detail_button_sanitas') : getLabel('detail_button')
})

const itemData = computed(() => {
  return props.item
})

const showBackButton = computed(() => {
  if (props.skipPackagesOverview) {
    return false
  }
  return !hasChosenPackage.value
})

// METHODS
function buttonAction() {
  const options = {
    item: props.item,
    target: props.openInSameWindow ? '_top' : '_blank',
  }
  return props.item.isPersonDataEdited ? emit('goToSanitas', options) : onEditPerson()
}

function onEditPerson() {
  personModal.value = true
}

async function savePerson(person) {
  // don't update basket if data was incomplete
  if (!person.address || !person.dateOfBirth) {
    return
  }

  isSavingPersonData.value = true

  const updatedPerson = cloneDeep(props.item.basket.persons[0])
  updatedPerson.personData = {
    ...updatedPerson.personData,
    dateOfBirth: person.dateOfBirth,
    gender: person.gender,
  }
  updatedPerson.contractModified = true

  try {
    const basketPayload = Object.assign({}, cloneDeep(props.item.basket), {
      address: person.address,
      persons: [updatedPerson],
    })

    emit('editScreenDefinitions', { isPersonDataEdited: true, showExampleOfferText: false })

    ReactivityUtil.reAssign(props.item.basket, await basketStore.updateOvpBasket(basketPayload, { simulate: true }))

    personModal.value = false
  } catch (e) {
    console.error(e)
    emit('editScreenDefinitions', { isPersonDataEdited: false })
  } finally {
    isSavingPersonData.value = false
  }
}

// EPILOGUE
provide('detailProps', { itemData })
</script>

<style scoped>
.Detail {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 570px;

  .back {
    font-size: var(--pw-font-size-small);
    position: absolute;
  }

  .detail {
    flex: 1 0 auto;

    .narrow & {
      height: 389px;
    }

    .wide & {
      height: auto;
    }
  }

  .update-person {
    font-size: var(--pw-font-size-small);
    margin: 0.5rem 0;

    .wide & {
      width: 600px;
      margin: 0.5rem auto;
    }
  }

  .actions {
    margin-top: 0.5rem;
    margin-bottom: 10px;

    .button-detail-loader {
      position: absolute;
      right: 0;
      top: 12px;
      z-index: 2;

      height: 35px;
      width: 35px;
      line-height: 24px;
      font-size: 20px;
      font-weight: 400;
      text-align: center;
      cursor: wait;

      span {
        display: inline-block;
        animation: spin 2s linear infinite;
      }
    }

    @keyframes spin {
      100% {
        -webkit-transform: rotate(360deg);
        transform: rotate(360deg);
      }
    }

    p {
      display: none;
    }

    .wide & {
      display: flex;
      align-items: center;
      width: 600px;
      margin: 0 auto;

      p {
        display: inherit;
        flex: 1;
        font-size: var(--pw-font-size-medium);
      }

      .button-detail {
        width: unset;
      }
    }

    .narrow & {
      .button-detail {
        width: 100%;
      }
    }
  }
}

/* move detail down, so title doesn't overlap back-link */
@media screen and (max-width: 299px) {
  .Detail {
    .detail {
      margin-top: 15px;
    }
  }
}
</style>
<style name="short" scoped>
@media screen and (max-height: 596px) {
  .Detail {
    height: 100%;
  }
}
</style>
